import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import ErrorBoundary from '../components/ErrorBoundary';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

/**
 * Support Page
 * Single Responsibility: Display support information and resources
 */
const Support = () => {
  const { isAuthenticated } = useAuth();
  const [message, setMessage] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const saveMessage = async (event) => {
    event.preventDefault();
    setError('');
    setSaved(false);
    const text = message.trim();
    if (!text) {
      setError('A message is required.');
      return;
    }
    setSending(true);
    try {
      await api.post('/support', { area: 'support', message: text });
      setSaved(true);
      setMessage('');
    } catch (err) {
      setError('The message was not saved.');
    } finally {
      setSending(false);
    }
  };
  return (
    <ErrorBoundary section="Support">
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Support Center</h1>
            <p className="text-xl text-gray-600">
              Get help, find answers, and learn how to make the most of eloNest
            </p>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Quick Help Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">FAQs</h3>
              <p className="text-gray-600 mb-4">
                Find answers to commonly asked questions about features, billing, and account management.
              </p>
              <Link to="/support/faq" className="text-blue-600 hover:text-blue-700 font-medium">
                Browse FAQs →
              </Link>
            </div>

            <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Documentation</h3>
              <p className="text-gray-600 mb-4">
                Comprehensive guides and tutorials to help you master all features of eloNest.
              </p>
              <Link to="/support/docs" className="text-blue-600 hover:text-blue-700 font-medium">
                View Docs →
              </Link>
            </div>

            <div className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Contact Us</h3>
              <p className="text-gray-600 mb-4">
                Need personalized help? Reach out to our support team for assistance.
              </p>
              <Link to="/contact" className="text-blue-600 hover:text-blue-700 font-medium">
                Contact Support →
              </Link>
            </div>
          </div>

          {/* Support Options */}
          <div className="bg-white rounded-lg shadow p-8 mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">How Can We Help?</h2>
            
            <div className="space-y-6">
              <div className="border-l-4 border-blue-500 pl-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Save a message</h3>
                <p className="text-gray-600 mb-2">
                  A signed-in person can save a support message here. This is not a live chat and it does not send an email.
                </p>
                {isAuthenticated ? (
                  <form onSubmit={saveMessage} className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700" htmlFor="support-message">
                      Message
                    </label>
                    <textarea
                      id="support-message"
                      value={message}
                      onChange={(event) => setMessage(event.target.value)}
                      rows={4}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900"
                    />
                    <button
                      type="submit"
                      disabled={sending}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50"
                    >
                      {sending ? 'Saving' : 'Save message'}
                    </button>
                    {saved && <p className="text-green-700">The message was saved.</p>}
                    {error && <p className="text-red-700" role="alert">{error}</p>}
                  </form>
                ) : (
                  <p className="text-gray-600">
                    <Link to="/login" className="text-blue-600 hover:text-blue-700 font-medium">Sign in</Link> to save a support message.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Common Issues */}
          <div className="bg-white rounded-lg shadow p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Common Issues & Solutions</h2>
            
            <div className="space-y-4">
              <details className="border border-gray-200 rounded-lg p-4">
                <summary className="font-semibold text-gray-900 cursor-pointer">
                  How do I reset my password?
                </summary>
                <p className="mt-2 text-gray-600">
                  Use the reset on the sign-in page.
                </p>
              </details>

              <details className="border border-gray-200 rounded-lg p-4">
                <summary className="font-semibold text-gray-900 cursor-pointer">
                  How do I import bank transactions?
                </summary>
                <p className="mt-2 text-gray-600">
                  Go to the Bank Imports section, select your account, and upload a CSV or Excel file from your bank.
                  The system will automatically match and categorize transactions.
                </p>
              </details>

              <details className="border border-gray-200 rounded-lg p-4">
                <summary className="font-semibold text-gray-900 cursor-pointer">
                  How do I generate an invoice PDF?
                </summary>
                <p className="mt-2 text-gray-600">
                  Navigate to Accounts Receivable, find the invoice you want to download, and click the "📄 PDF" button.
                  The PDF will include your business logo if you've uploaded one.
                </p>
              </details>

              <details className="border border-gray-200 rounded-lg p-4">
                <summary className="font-semibold text-gray-900 cursor-pointer">
                  Can I manage multiple businesses?
                </summary>
                <p className="mt-2 text-gray-600">
                  Yes! You can create and manage multiple businesses from a single account. Each business has its own
                  finances, invoices, and reports.
                </p>
              </details>

              <details className="border border-gray-200 rounded-lg p-4">
                <summary className="font-semibold text-gray-900 cursor-pointer">
                  How do I cancel my subscription?
                </summary>
                <p className="mt-2 text-gray-600">
                  Go to your account settings, navigate to the Subscription section, and click "Cancel Subscription".
                  Your subscription will remain active until the end of the current billing period.
                </p>
              </details>
            </div>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default Support;

